import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AiHubAuthModule } from "@aihub/core/authorization";
import { AiHubLoginPageModule } from "@aihub/ui/login-page";
import { AihubTooltipModule } from "@aihub/ui/tooltip";
import { MatSelectModule } from "@angular/material/select";
import { SharedModule } from "../../shared/src/public-api";
import { DeloitteAiPageModule } from "../../shared/src/lib/page";
import { DeloitteAiHeaderModule } from "@shared/header";
import { DeloitteAiFooterModule } from "@shared/footer";
import { AiHubLoaderModule } from "@aihub/ui/loader";
import { DeloitteAiBannerModule } from "@shared/banner";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { ReactiveFormsModule } from "@angular/forms";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatCardModule } from "@angular/material/card";
import { AppComponent } from "./app.component";
import { ChatThreadComponent } from "./chat-thread/chat-thread.component";
import { AppRoutingModule } from "./app-routing.module";
import { MatButtonModule } from "@angular/material/button";
import { environment } from "@environments/environment";

import { afterLoginHook } from "./utils/after-login-hook";
import { RunScriptsDirective } from "./utils/run-scripts.directive";
import { SafePipe } from "./utils/safe.pipe";

// Translation
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { InternationalizationModule } from "./internationalization/internationalization.module";

import { MainComponent } from "./main/main.component";
import { SideMenuComponent } from "./side-menu/side-menu.component";
import { ChatTitleComponent } from "./chat-title/chat-title.component";
import { NgxFileDropModule } from "ngx-file-drop";
import { AudioComponent } from "./audio/audio.component";
import { AudioRecordingService } from "./audio-recording.service";
import { BizbrainComponent } from "./input/bizbrain/bizbrain.component";
import { ConversationComponent } from "./conversation/conversation.component";
import { BibrainOutputComponent } from "./output/bibrain-output/bibrain-output.component";
import { ShortifyOutputComponent } from "./output/shortify-output/shortify-output.component";
import { IqgenOutputComponent } from "./output/iqgen-output/iqgen-output.component";
import { EstimateOutputComponent } from "./output/estimate-output/estimate-output.component";
import { PlanitOutputComponent } from "./output/planit-output/planit-output.component";
import { DeepProcessOutputComponent } from "./output/deep-process-output/deep-process-output.component";
import { KbinOutputComponent } from "./output/kbin-output/kbin-output.component";
import { PostgenieOutputComponent } from "./output/postgenie-output/postgenie-output.component";
import { DeepProcessComponent } from "./input/deep-process/deep-process.component";
import { ShortifyComponent } from "./input/shortify/shortify.component";
import { IqgenComponent } from "./input/iqgen/iqgen.component";
import { KbinComponent } from "./input/kbin/kbin.component";
import { PostgenieComponent } from "./input/postgenie/postgenie.component";
import { PlanitComponent } from "./input/planit/planit.component";
import { EstimateComponent } from "./input/estimate/estimate.component";
import { AiHubSelectModule } from "@aihub/ui/select";
import { MatTableModule } from "@angular/material/table";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatChipsModule } from "@angular/material/chips";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatTabsModule } from "@angular/material/tabs";
import { MarkdownModule } from "ngx-markdown";
import { MatMenuModule } from "@angular/material/menu";

import { TextToSpeechService } from "./text-to-speech.service";

import { BizbrainWarningDialog } from "./input/bizbrain/bizbrain-warning-dialog";
import { BizbrainModule } from "./input/bizbrain/bizbrain.module";
import { EstimateChartComponent } from "./output/estimate-output/tab-components/estimate-chart/estimate-chart.component";
import { EstimateTableComponent } from "./output/estimate-output/tab-components/estimate-table/estimate-table.component";
import { EstimateClassificationComponent } from "./output/estimate-output/tab-components/estimate-classification/estimate-classification.component";
import { EstimateLoadingComponent } from "./output/estimate-output/tab-components/estimate-loading/estimate-loading.component";
import { EstimateTimeframeComponent } from "./output/estimate-output/tab-components/estimate-timeframe/estimate-timeframe.component";
import { DialogueComponent } from "./output/estimate-output/estimate-dialogue/estimate-dialogue.component";

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, "../assets/locales/", ".json");
}

@NgModule({
  declarations: [
    AppComponent,
    ChatThreadComponent,
    MainComponent,
    SideMenuComponent,
    ChatTitleComponent,
    AudioComponent,
    BizbrainComponent,
    ConversationComponent,
    BibrainOutputComponent,
    RunScriptsDirective,
    SafePipe,
    ShortifyOutputComponent,
    IqgenOutputComponent,
    EstimateOutputComponent,
    PlanitOutputComponent,
    DeepProcessOutputComponent,
    KbinOutputComponent,
    PostgenieOutputComponent,
    DeepProcessComponent,
    ShortifyComponent,
    IqgenComponent,
    KbinComponent,
    PostgenieComponent,
    PlanitComponent,
    EstimateComponent,
    EstimateChartComponent,
    EstimateTableComponent,
    EstimateClassificationComponent,
    EstimateLoadingComponent,
    EstimateTimeframeComponent,
    BizbrainWarningDialog,
    DialogueComponent,
  ],
  imports: [
    BrowserModule,
    BizbrainModule,
    MatSlideToggleModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    HttpClientModule,
    AppRoutingModule,
    MatProgressBarModule,
    BrowserAnimationsModule,
    NgxFileDropModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatExpansionModule,
    MatCardModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatTableModule,
    MatGridListModule,
    MatChipsModule,
    MatTooltipModule,
    MatTabsModule,
    AihubTooltipModule,
    AiHubSelectModule,
    MarkdownModule.forRoot(),
    AiHubAuthModule.forRoot({
      firebase: environment.firebase,
      params: environment.authParams,
      signInType: environment.signInType,
      jwtInterceptHook: (uri: string) =>
        uri.startsWith(environment.subscriptionServiceBaseURL) ||
        uri.startsWith(environment.chatBackendBaseURL),
    }),
    AiHubLoginPageModule.forRoot(false, afterLoginHook),
    InternationalizationModule.forRoot({ locale_id: "en-US" }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    MatSelectModule,
    SharedModule,
    DeloitteAiPageModule,
    DeloitteAiHeaderModule,
    DeloitteAiFooterModule,
    AiHubLoaderModule,
    DeloitteAiBannerModule,
  ],
  providers: [AudioRecordingService, TextToSpeechService],
  bootstrap: [AppComponent],
})
export class AppModule {}
